import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GAME_META } from '../../core/gameRegistry';
import { SINGLE_PLAYER_GAMES } from './SoloSection';
import { designTokens } from '../../theme/theme';

export function HelpSection() {
  return (
              <Box sx={{ p: { xs: 1, sm: 2 }, color: 'white' }}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 900, color: 'var(--app-success)' }}>
                  📘 About Celebrations
                </Typography>
                <Typography sx={{ color: '#9aa4af', fontSize: '0.88rem', lineHeight: 1.6, mb: 2.5 }}>
                  Play multiplayer party games with friends or jump into a solo game. Expand a game below for instructions and see the shared features available across the app.
                </Typography>
                <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', mb: 0.8 }}>
                  GETTING STARTED
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                  <Accordion disableGutters elevation={0} sx={{
                    bgcolor: 'rgba(255,255,255,0.045)', color: 'var(--app-text-primary)',
                    borderLeft: '3px solid var(--app-primary)', borderRadius: '10px !important',
                    '&:before': { display: 'none' },
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--app-text-secondary)' }} />}>
                      <Typography sx={{ fontWeight: 800, color: 'var(--app-primary)' }}>
                        🎮 Start or join a multiplayer game
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, color: '#aaa', fontSize: '0.86rem', lineHeight: 1.7 }}>
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        <li>To host, choose <b>Create</b>, pick a game, adjust its settings, and create the room.</li>
                        <li>Share the room code shown in the lobby with your friends. They choose <b>Join</b>, enter the code, and join your room.</li>
                        <li>When enough players have joined, the host presses <b>Start Game</b>. Everyone sees a synchronized countdown before play begins.</li>
                      </ol>
                    </AccordionDetails>
                  </Accordion>
                  <Accordion disableGutters elevation={0} sx={{
                    bgcolor: 'rgba(255,255,255,0.045)', color: 'var(--app-text-primary)',
                    borderLeft: '3px solid var(--app-success)', borderRadius: '10px !important',
                    '&:before': { display: 'none' },
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--app-text-secondary)' }} />}>
                      <Typography sx={{ fontWeight: 800, color: 'var(--app-success)' }}>
                        🎙️ Use game voice chat
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, color: '#aaa', fontSize: '0.86rem', lineHeight: 1.7 }}>
                      Voice chat is optional during an active multiplayer game. Choose <b>Join voice</b> and allow microphone access when your browser asks. The microphone and hang-up icons let you mute or leave voice chat; leaving voice does not leave the game. Voice controls and game audio controls stay together in the bottom dock. Voice chat requires the app’s LiveKit voice service to be configured.
                    </AccordionDetails>
                  </Accordion>
                </Box>
                <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', mb: 0.8 }}>
                  SHARED GAME FEATURES
                </Typography>
                <Box sx={{
                  mb: 2.5, p: 1.5, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.035)',
                  border: '1px solid var(--app-border-medium)',
                }}>
                  <Box component="ul" sx={{
                    m: 0, pl: 2.5, color: '#aaa',
                    fontSize: '0.84rem', lineHeight: 1.75,
                    '& li + li': { mt: 0.7 },
                  }}>
                    <li><b style={{ color: 'var(--app-text-primary)' }}>Game audio:</b> Toggle background music and sound effects independently from the bottom dock. Sound effects include game feedback and multiplayer countdowns.</li>
                    <li><b style={{ color: 'var(--app-text-primary)' }}>Mobile-friendly layout:</b> Game content reserves space for the shared controls so they stay accessible without covering play.</li>
                    <li><b style={{ color: 'var(--app-text-primary)' }}>Reconnect:</b> Reopen the app or reconnect after going offline to return to a multiplayer session while its room is still available.</li>
                  </Box>
                </Box>
                <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', mb: 0.8 }}>
                  FAMILY TREE
                </Typography>
                <Typography sx={{ color: '#aaa', fontSize: '0.86rem', lineHeight: 1.65, mb: 2.5 }}>
                  Build a private family tree from the navigation menu. Add and edit relatives, connect parents and partners, save life dates, birth country, profession, stories, and photos, and choose the tree style, date format, and text size. Search, zoom, reset the view, or export the connected tree as SVG. A Google account is required to save your tree.
                </Typography>
                <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', mb: 0.8 }}>
                  MULTIPLAYER GAMES
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(GAME_META).map(([id, meta]) => (
                    <Accordion key={id} disableGutters elevation={0} sx={{
                      bgcolor: 'rgba(255,255,255,0.045)', color: 'var(--app-text-primary)',
                      borderLeft: `3px solid ${meta.color || designTokens.colors.primary}`,
                      borderRadius: '10px !important',
                      '&:before': { display: 'none' },
                    }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--app-text-secondary)' }} />}>
                        <Typography sx={{ fontWeight: 800, color: meta.color || designTokens.colors.primary }}>
                          {meta.icon} {meta.label}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, color: '#aaa', fontSize: '0.86rem', lineHeight: 1.65 }}>
                        {meta.howToPlay || meta.description}
                        {id === 'uno' && (
                          <Typography component="p" sx={{ mt: 1, color: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}>
                            Each turn has a 10-second timer. If you do not play or draw in time, a card is drawn automatically. Four consecutive timed-out turns remove the player from the room.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
                <Typography sx={{ color: 'var(--app-text-secondary)', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em', mt: 2.5, mb: 0.8 }}>
                  SOLO GAMES
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {SINGLE_PLAYER_GAMES.map(game => (
                    <Accordion key={game.id} disableGutters elevation={0} sx={{
                      bgcolor: 'rgba(255,255,255,0.045)', color: 'var(--app-text-primary)',
                      borderLeft: `3px solid ${game.color}`,
                      borderRadius: '10px !important',
                      '&:before': { display: 'none' },
                    }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--app-text-secondary)' }} />}>
                        <Typography sx={{ fontWeight: 800, color: game.color }}>
                          {game.icon} {game.label}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, color: '#aaa', fontSize: '0.86rem', lineHeight: 1.65 }}>
                        {game.howToPlay}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
                <Box sx={{
                  mt: 3, p: 1.5, textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.035)',
                  border: '1px solid var(--app-border-medium)',
                  borderRadius: 2,
                }}>
                  <Typography sx={{ color: '#9aa4af', fontSize: '0.78rem' }}>
                    Created by <Box component="span" sx={{ color: 'var(--app-text-primary)', fontWeight: 800 }}>
                      Bhanu Merakanapalli
                    </Box>
                  </Typography>
                  <Typography
                    component="a"
                    href="https://calmindian.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block', mt: 0.4, color: 'var(--app-success)',
                      fontSize: '0.78rem', fontWeight: 700,
                      textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    calmindian.com
                  </Typography>
                </Box>
              </Box>
  );
}
